import type { V2_MetaFunction, DataFunctionArgs } from '@remix-run/node'
import type { SocialIcon } from '~/components/social-icons.tsx'

import { clsx } from 'clsx'
import { useFetcher } from '@remix-run/react'
import { json, redirect } from '@remix-run/node'
import { z } from 'zod'

import { prisma } from '~/utils/db.server.ts'
import { sendEmail } from '~/utils/email.server.ts'
import { decrypt, encrypt } from '~/utils/encryption.server.ts'
import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import { getDomainUrl } from '~/utils/misc.server.ts'
import { commitSession, getSession } from '~/utils/session.server.ts'
import { emailSchema } from '~/utils/user-validation.ts'
import { ErrorList, Field } from '~/utils/forms.tsx'

import { Button } from '~/components/button.tsx'
import { Container } from '~/components/container.tsx'
import {
	TwitterIcon,
	InstagramIcon,
	GitHubIcon,
	LinkedInIcon,
} from '~/components/social-icons.tsx'
import logoGoldmanSachs from '~/images/logos/goldman-sachs.svg'
import logoGrowthStreet from '~/images/logos/growth-street.png'
import logoSpectacles from '~/images/logos/spectacles.svg'
import logoTheCollective from '~/images/logos/the-collective.svg'

export function formatDate(dateString: string) {
	return new Date(`${dateString}T00:00:00Z`).toLocaleDateString('en-US', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		timeZone: 'UTC',
	})
}

function MailIcon(props: { [key: string]: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			{...props}
		>
			<path
				d="M2.75 7.75a3 3 0 0 1 3-3h12.5a3 3 0 0 1 3 3v8.5a3 3 0 0 1-3 3H5.75a3 3 0 0 1-3-3v-8.5Z"
				className="fill-zinc-100 stroke-zinc-400 dark:fill-zinc-100/10 dark:stroke-zinc-500"
			/>
			<path
				d="m4 6 6.024 5.479a2.915 2.915 0 0 0 3.952 0L20 6"
				className="stroke-zinc-400 dark:stroke-zinc-500"
			/>
		</svg>
	)
}

function BriefcaseIcon(props: { [key: string]: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			{...props}
		>
			<path
				d="M2.75 9.75a3 3 0 0 1 3-3h12.5a3 3 0 0 1 3 3v8.5a3 3 0 0 1-3 3H5.75a3 3 0 0 1-3-3v-8.5Z"
				className="fill-zinc-100 stroke-zinc-400 dark:fill-zinc-100/10 dark:stroke-zinc-500"
			/>
			<path
				d="M3 14.25h6.249c.484 0 .952-.002 1.316.319l.777.682a.996.996 0 0 0 1.316 0l.777-.682c.364-.32.832-.319 1.316-.319H21M8.75 6.5V4.75a2 2 0 0 1 2-2h2.5a2 2 0 0 1 2 2V6.5"
				className="stroke-zinc-400 dark:stroke-zinc-500"
			/>
		</svg>
	)
}

function ArrowDownIcon(props: { [key: string]: string }) {
	return (
		<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" {...props}>
			<path
				d="M4.75 8.75 8 12.25m0 0 3.25-3.5M8 12.25v-8.5"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

function SocialLink({
	icon: Icon,
	...props
}: {
	icon: (props: SocialIcon) => JSX.Element
	href?: string
	'aria-label'?: string
	target?: string
}) {
	return (
		<a className="group -m-1 p-1" {...props}>
			<Icon className="h-6 w-6 fill-zinc-500 transition group-hover:fill-zinc-600 dark:fill-zinc-400 dark:group-hover:fill-zinc-300" />
		</a>
	)
}

function Resume() {
	interface Role {
		company: string
		title: string
		logo: string
		start: string | { label: string; dateTime: string }
		end: string | { label: string; dateTime: string }
	}

	const isString = (
		value: string | { label: string; dateTime: string },
	): value is string => typeof value === 'string'

	let resume = [
		{
			company: 'Spectacles',
			title: 'Co-Founder',
			logo: logoSpectacles,
			start: '2019',
			end: {
				label: 'Present',
				dateTime: new Date().getFullYear().toString(),
			},
		},
		{
			company: 'Goldman Sachs',
			title: 'Software Engineer',
			logo: logoGoldmanSachs,
			start: '2018',
			end: {
				label: 'Present',
				dateTime: new Date().getFullYear().toString(),
			},
		},
		{
			company: 'The Collective',
			title: 'Software Engineeer',
			logo: logoTheCollective,
			start: '2017',
			end: '2018',
		},
		{
			company: 'Growth Street',
			title: 'Software Engineer',
			logo: logoGrowthStreet,
			start: '2016',
			end: '2017',
		},
	]

	return (
		<div className="rounded-2xl border border-zinc-100 p-6 dark:border-zinc-700/40">
			<h2 className="flex text-sm font-semibold text-zinc-900 dark:text-zinc-100">
				<BriefcaseIcon className="h-6 w-6 flex-none" />
				<span className="ml-3">Work</span>
			</h2>
			<ol className="mt-6 space-y-4">
				{resume.map((role: Role, roleIndex) => (
					<li key={roleIndex} className="flex gap-4">
						<div className="relative mt-1 flex h-10 w-10 flex-none items-center justify-center rounded-full shadow-md shadow-zinc-800/5 ring-1 ring-zinc-900/5 dark:border dark:border-zinc-700/50 dark:bg-zinc-800 dark:ring-0">
							<img src={role.logo} alt="" className="h-7 w-7" />
						</div>
						<dl className="flex flex-auto flex-wrap gap-x-2">
							<dt className="sr-only">Company</dt>
							<dd className="w-full flex-none text-sm font-medium text-zinc-900 dark:text-zinc-100">
								{role.company}
							</dd>
							<dt className="sr-only">Role</dt>
							<dd className="text-xs text-zinc-500 dark:text-zinc-400">
								{role.title}
							</dd>
							<dt className="sr-only">Date</dt>
							<dd
								className="ml-auto text-xs text-zinc-400 dark:text-zinc-500"
								aria-label={`${
									isString(role.start) ? role.start : role.start.label
								} until ${isString(role.end) ? role.end : role.end.label}`}
							>
								<time
									dateTime={
										isString(role.start) ? role.start : role.start.dateTime
									}
								>
									{isString(role.start) ? role.start : role.start.label}
								</time>{' '}
								<span aria-hidden="true">—</span>{' '}
								<time
									dateTime={isString(role.end) ? role.end : role.end.dateTime}
								>
									{isString(role.end) ? role.end : role.end.label}
								</time>
							</dd>
						</dl>
					</li>
				))}
			</ol>
			<Button
				href="https://drive.google.com/file/d/1hMvU62189RspeqoP8l0v_o1hIhNZNkbg/view?usp=sharing"
				variant="secondary"
				className="group mt-6 w-full"
			>
				Download CV
				<ArrowDownIcon className="h-4 w-4 stroke-zinc-400 transition group-active:stroke-zinc-600 dark:group-hover:stroke-zinc-50 dark:group-active:stroke-zinc-50" />
			</Button>
		</div>
	)
}

function Photos() {
	let rotations = ['rotate-2', '-rotate-2', 'rotate-2', 'rotate-2', '-rotate-2']

	const images = [
		'https://res.cloudinary.com/miller-com/image/upload/v1672400016/headshot-1_yiumlp.jpg',
		'https://res.cloudinary.com/miller-com/image/upload/v1672400016/headshot-2_wqpdku.jpg',
		'https://res.cloudinary.com/miller-com/image/upload/v1672400016/headshot-3_f375ki.jpg',
		'https://res.cloudinary.com/miller-com/image/upload/v1672400016/headshot-4_h3vqbx.jpg',
		'https://res.cloudinary.com/miller-com/image/upload/v1672400016/headshot-5_xc8hid.jpg',
	]
	return (
		<div className="mt-16 sm:mt-20">
			<div className="-my-4 flex justify-center gap-5 overflow-hidden py-4 sm:gap-8">
				{images.map((image, imageIndex) => (
					<div
						key={image}
						className={clsx(
							'relative aspect-[9/10] w-44 flex-none overflow-hidden rounded-xl bg-zinc-100 dark:bg-zinc-800 sm:w-72 sm:rounded-2xl',
							rotations[imageIndex % rotations.length],
						)}
					>
						<img
							src={image}
							alt=""
							sizes="(min-width: 640px) 18rem, 11rem"
							className="absolute inset-0 h-full w-full object-cover"
						/>
					</div>
				))}
			</div>
		</div>
	)
}

export const subscribeEmailSessionKey = 'subscriptionToken'
const subscribeTokenQueryParam = 'token'
const tokenType = 'subscribe'

function createSchema(
	constraints: {
		isEmailUnique: (email: string) => Promise<boolean>
	} = {
		isEmailUnique: async () => true,
	},
) {
	const signupSchema = z.object({
		email: emailSchema.superRefine((email, ctx) => {
			// if constraint is not defined, throw an error
			if (typeof constraints.isEmailUnique === 'undefined') {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: conform.VALIDATION_UNDEFINED,
				})
			}
			// if constraint is defined, validate uniqueness
			return constraints.isEmailUnique(email).then(isUnique => {
				if (isUnique) {
					return
				}
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'A user already exists with this email',
				})
			})
		}),
	})
	return signupSchema
}

const tokenSchema = z.object({
	type: z.literal(tokenType),
	payload: z.object({
		email: emailSchema,
	}),
})

export async function loader({ request }: DataFunctionArgs) {
	const subscribeTokenString = new URL(request.url).searchParams.get(
		subscribeTokenQueryParam,
	)
	if (subscribeTokenString) {
		const result = tokenSchema.safeParse(
			JSON.parse(decrypt(subscribeTokenString)),
		)
		if (!result.success) return redirect('/')
		const token = result.data

		const session = await getSession(request.headers.get('cookie'))
		session.set(subscribeEmailSessionKey, token.payload.email)
		return redirect('/subscribe', {
			headers: {
				'Set-Cookie': await commitSession(session),
			},
		})
	}
	return json({})
}

export async function action({ request }: DataFunctionArgs) {
	const formData = await request.formData()
	const submission = await parse(formData, {
		schema: () =>
			createSchema({
				async isEmailUnique(email: string) {
					const existingUser = await prisma.user.findUnique({
						where: { email },
						select: { id: true },
					})
					return !existingUser
				},
			}),
		acceptMultipleErrors: () => true,
		async: true,
	})
	if (!submission.value || submission.intent !== 'submit') {
		return json(
			{
				status: 'error',
				submission,
			} as const,
			{ status: 400 },
		)
	}

	const { email } = submission.value

	const subscribeToken = encrypt(
		JSON.stringify({ type: tokenType, payload: { email } }),
	)
	const subscribeUrl = new URL(`${getDomainUrl(request)}/`)
	subscribeUrl.searchParams.set(subscribeTokenQueryParam, subscribeToken)

	const response = await sendEmail({
		to: email,
		subject: `Welcome to Miller Musings!`,
		text: `Please open this URL: ${subscribeUrl}`,
		html: `
		<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
		<html>
			<head>
				<meta http-equiv="Content-Type" content="text/html charset=UTF-8" />
			</head>
			<body>
				<h1>Welcome to Miller Musings!</h1>
				<p>Click the link below to get started:</p>
				<a href="${subscribeUrl}">${subscribeUrl}</a>
			</body>
		</html>
		`,
	})

	if (response.status === 'success') {
		return json({ status: 'success', submission } as const)
	} else {
		submission.error[''] = response.error.message
		return json(
			{
				status: 'error',
				submission,
			} as const,
			{ status: 500 },
		)
	}
}

export const meta: V2_MetaFunction = () => [
	{ title: 'Tom Miller - Software developer, founder, and cyclist' },
	{
		name: 'description',
		content:
			"I’m Tom, a software developer and entrepreneur based in London. I’m a founder of Spectacles, where we're building the first continuous integration tool for testing and validating Looker & LookML.",
	},
]

export default function Index() {
	const subscribeFetcher = useFetcher<typeof action>()
	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getFieldsetConstraint(createSchema()),
		lastSubmission: subscribeFetcher.data?.submission,
		onValidate({ formData }) {
			return parse(formData, { schema: createSchema() })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<>
			<Container className="mt-9">
				<div className="max-w-2xl">
					<h1 className="text-4xl font-bold tracking-tight text-zinc-800 dark:text-zinc-100 sm:text-5xl">
						Software developer, founder, and amateur cyclist.
					</h1>
					<p className="mt-6 text-base text-zinc-600 dark:text-zinc-400">
						I'm Tom, a software developer and entrepreneur based in London. I'm
						a founder of Spectacles, where we're building the first continuous
						integration tool for testing and validating Looker & LookML.
					</p>
					<div className="mt-6 flex gap-6">
						<SocialLink
							href="https://twitter.com/twin_millers"
							target="_blank"
							aria-label="Follow on Twitter"
							icon={TwitterIcon}
						/>
						<SocialLink
							href="https://www.instagram.com/fakejamesmilller"
							target="_blank"
							aria-label="Follow on Instagram"
							icon={InstagramIcon}
						/>
						<SocialLink
							href="https://github.com/Hawk94"
							target="_blank"
							aria-label="Follow on GitHub"
							icon={GitHubIcon}
						/>
						<SocialLink
							href="https://www.linkedin.com/in/tommiller-"
							target="_blank"
							aria-label="Follow on LinkedIn"
							icon={LinkedInIcon}
						/>
					</div>
				</div>
			</Container>
			<Photos />
			<Container className="mt-24 md:mt-28">
				<div className="mx-auto grid max-w-xl grid-cols-1 gap-y-20 lg:max-w-none lg:grid-cols-2">
					<div className="flex flex-col gap-16">
						<subscribeFetcher.Form
							method="post"
							className="rounded-2xl border border-zinc-100 p-6 dark:border-zinc-700/40"
							{...form.props}
						>
							<h2 className="flex text-sm font-semibold text-zinc-900 dark:text-zinc-100">
								<MailIcon className="h-6 w-6 flex-none" />
								<span className="ml-3">Stay up to date</span>
							</h2>
							<p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
								Get notified when I publish something new, and unsubscribe at
								any time.
							</p>
							<div className="mt-6 flex">
								<Field
									labelProps={{
										htmlFor: fields.email.id,
									}}
									inputProps={fields.email}
									errors={fields.email.errors}
									className="min-w-0 flex-auto "
								/>
								<ErrorList errors={form.errors} id={form.errorId} />
								<Button type="submit" className="ml-4 h-[36px] flex-none">
									Join
								</Button>
							</div>
						</subscribeFetcher.Form>
					</div>
					<div className="space-y-10 lg:pl-16 xl:pl-24">
						<Resume />
					</div>
				</div>
			</Container>
		</>
	)
}
